
import React, { useState, useEffect } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, ConfirmModal, Button as CommonButton } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';
import {
    Box, Typography, Tooltip, IconButton, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid,
    TextField, InputAdornment, Stack, Avatar, Menu, MenuItem, ListItemIcon, ListItemText,
    Divider, Card, CardContent, CircularProgress, FormControl, OutlinedInput, InputLabel, Paper,
    Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel, FormHelperText
} from '@mui/material';
import {
    Edit, DeleteOutline, CheckCircle, ErrorOutline, Refresh, Star as StarIcon,
    Search, FilterList, Close, Save, AddLink, ExpandMore as ExpandMoreIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    MoreVert as MoreVertIcon,
    Add as AddIcon,
    ContentCopy as ContentCopyIcon,
    Visibility as ViewIcon,
    VisibilityOff,
    Key as KeyIcon,
    Send as SendIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ServiceTypesManager from './ServiceTypesManager.jsx';
import { SERVICE_CATALOG as STATIC_CATALOG, getServiceMeta as getStaticMeta } from '../config/serviceCatalog.js';

const getServiceMeta = (serviceId, catalog) => {
    if (catalog?.length) {
        const found = catalog.find(s => (s.service_id || s.id) === serviceId);
        if (found) return found;
    }
    return getStaticMeta(serviceId);
};

const DynamicField = ({ field, value, onChange, isEditing }) => {
    const [showPwd, setShowPwd] = useState(false);
    if (field.type === 'password') {
        return (
            <FormControl fullWidth size="small">
                <InputLabel>{field.label}{field.required && ' *'}</InputLabel>
                <OutlinedInput
                    type={showPwd ? 'text' : 'password'}
                    value={value || ''}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    label={`${field.label}${field.required ? ' *' : ''}`}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPwd(p => !p)} edge="end">
                                {showPwd ? <VisibilityOff fontSize="small" /> : <ViewIcon fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    }
                />
                {isEditing && (
                    <FormHelperText>Leave as ******** to keep existing secret unchanged.</FormHelperText>
                )}
            </FormControl>
        );
    }
    return (
        <TextField label={`${field.label}${field.required ? ' *' : ''}`} size="small" fullWidth
            type={field.type || 'text'} value={value || ''}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder={field.required ? 'Required' : 'Optional'}
        />
    );
};

const ApiKeyFormDialog = ({ open, onClose, onSave, editingKey, catalog }) => {
    const [step, setStep] = useState(editingKey ? 'form' : 'pick');
    const [selected, setSelected] = useState(editingKey ? getServiceMeta(editingKey.service, catalog) : null);
    const [label, setLabel] = useState(editingKey?.label || '');
    const [isDefault, setIsDefault] = useState(editingKey ? !!editingKey.is_default : false);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);

    const effectiveCatalog = catalog?.length ? catalog : STATIC_CATALOG;

    useEffect(() => {
        if (open) {
            setStep(editingKey ? 'form' : 'pick');
            setSelected(editingKey ? getServiceMeta(editingKey.service, catalog) : null);
            setLabel(editingKey?.label || '');
            setIsDefault(editingKey ? !!editingKey.is_default : false);
            setValues(editingKey?.key_values || {});
            setSaving(false);
        }
    }, [open, editingKey, catalog]);

    const handleClose = () => {
        setStep(editingKey ? 'form' : 'pick');
        setSelected(editingKey ? getServiceMeta(editingKey.service, catalog) : null);
        setLabel(editingKey?.label || '');
        setIsDefault(editingKey ? !!editingKey.is_default : false);
        setValues({});
        onClose();
    };
    const handlePick = (svc) => { setSelected(svc); setLabel(editingKey?.label || `Platform ${svc.label}`); setIsDefault(false); setStep('form'); };
    const handleSave = async () => {
        if (!label.trim()) return;
        setSaving(true);
        const serviceKey = selected.service_id || selected.id;
        await onSave({ service: serviceKey, label, is_default: isDefault, key_values: values });
        setSaving(false); handleClose();
    };
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{step === 'pick' ? 'Select a Service' : (editingKey ? `Edit ${selected?.label}` : `Add ${selected?.label} Key`)}</DialogTitle>
            <DialogContent>
                {step === 'pick' ? (
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        {effectiveCatalog.map(svc => (
                            <Grid item xs={6} key={svc.service_id || svc.id}>
                                <Card onClick={() => handlePick(svc)} sx={{ cursor: 'pointer', border: '1px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '12px !important' }}>
                                        <Avatar variant="rounded" sx={{ bgcolor: `${svc.color}18`, width: 36, height: 36 }}>
                                            <DynamicIcon name={svc.icon} className="h-5 w-5" style={{ color: svc.color }} />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">{svc.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">{svc.description}</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {!editingKey && <Button size="small" onClick={() => setStep('pick')} sx={{ alignSelf: 'flex-start' }}>← Back</Button>}
                        <TextField label="Label *" size="small" fullWidth value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`e.g. Platform ${selected?.label}`} helperText="Friendly name for this key" />
                        {selected?.fields.map(f => <DynamicField key={f.key} field={f} value={values[f.key]} onChange={(k, v) => setValues(p => ({ ...p, [k]: v }))} isEditing={!!editingKey} />)}
                        <FormControlLabel
                            control={<Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} color="primary" />}
                            label={<Typography variant="body2">Set as default service for this category</Typography>}
                        />
                    </Stack>
                )}
            </DialogContent>
            {step === 'form' && (
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !label.trim()}
                        startIcon={saving ? <CircularProgress size={14} /> : null}>
                        {editingKey ? 'Update Key' : 'Save Key'}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Platform API Keys Section
// ─────────────────────────────────────────────────────────────────────────────
const PlatformApiKeysSection = ({ token, embedded }) => {
    const theme = useTheme();
    const { addNotification } = useNotification();
    const { data: apiKeys, isLoading, refresh } = useData('/integrations/platform/api-keys', token);
    const { data: catalog } = useData('/integrations/platform/service-types', token);
    const [formOpen, setFormOpen] = useState(false);
    const [editingKey, setEditingKey] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuTarget, setMenuTarget] = useState(null);

    const handleSave = async ({ service, label, is_default, key_values }) => {
        try {
            if (editingKey) {
                const payload = { label, is_default }; // Include is_default
                const filled = Object.fromEntries(Object.entries(key_values).filter(([, v]) => v !== undefined && v !== '********'));
                payload.key_values = filled;
                await apiFetch(`/integrations/platform/api-keys/${editingKey.id}`, token, { method: 'PUT', body: JSON.stringify(payload) });
                addNotification('Platform key updated.', 'success');
            } else {
                await apiFetch('/integrations/platform/api-keys', token, { method: 'POST', body: JSON.stringify({ service, label, is_default, key_values }) });
                addNotification('Platform key saved.', 'success');
            }
            refresh();
        } catch (e) { addNotification(e.message || 'Failed to save.', 'error'); }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/integrations/platform/api-keys/${confirmDelete.id}`, token, { method: 'DELETE' });
            addNotification('Platform key deleted.', 'success'); refresh();
        } catch (e) { addNotification(e.message || 'Failed to delete.', 'error'); }
        finally { setConfirmDelete(null); }
    };

    return (
        <Box sx={{ mb: embedded ? 0 : 4 }}>
            <Stack direction="row" justifyContent={embedded ? 'flex-end' : 'space-between'} alignItems="center" sx={{ mb: 2 }}>
                {!embedded && (
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <KeyIcon fontSize="small" color="primary" />
                            <Typography variant="h6">Platform Integrations</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Securely configure connections shared across all tenants that do not override them.
                        </Typography>
                    </Box>
                )}
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingKey(null); setFormOpen(true); }}>Add Integration</Button>
            </Stack>

            {isLoading ? <LoadingSpinner /> : !apiKeys?.length ? (
                <Box sx={{ textAlign: 'center', py: 5, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                    <KeyIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No Integration services or platform keys configured. Default Platform Services will be used.</Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {apiKeys.map(k => {
                        const meta = getServiceMeta(k.service, catalog);
                        return (
                            <Grid item xs={12} sm={6} md={4} key={k.id}>
                                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4], borderColor: 'primary.main' } }}>
                                    <Box sx={{ p: 2 }}>
                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                            <Avatar variant="rounded" sx={{ bgcolor: `${meta.color}18`, width: 48, height: 48 }}>
                                                <DynamicIcon name={meta.icon} className="h-6 w-6" style={{ color: meta.color }} />
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="subtitle1" fontWeight="bold" noWrap>{k.label}</Typography>
                                                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                    <Chip label={meta.label} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${meta.color}18`, color: meta.color, fontWeight: 700 }} />
                                                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="text.secondary">••••••••</Typography>
                                                </Stack>
                                            </Box>
                                            <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(k); }}>
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip icon={<CheckCircle sx={{ fontSize: '14px !important' }} />} label="Active" size="small" color="success" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                                            {k.is_default && (
                                                <Chip icon={<StarIcon sx={{ fontSize: '14px !important' }} />} label="Default Category Service" size="small" color="primary" sx={{ height: 22, fontSize: '0.7rem' }} />
                                            )}
                                        </Stack>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => { setMenuAnchor(null); setEditingKey(menuTarget); setFormOpen(true); }}>
                    <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Edit</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setMenuAnchor(null); setConfirmDelete(menuTarget); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon><ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            <ApiKeyFormDialog open={formOpen} onClose={() => { setFormOpen(false); setEditingKey(null); }} onSave={handleSave} editingKey={editingKey} catalog={catalog} />
            <ConfirmModal isOpen={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={handleDelete} title={`Delete "${confirmDelete?.label}"?`}>
                All tenants using this platform key will fall back to .env values.
            </ConfirmModal>
        </Box>
    );
};


const GlobalIntegrationsSettings = ({ token }) => {
    return (
        <Box sx={{ maxWidth: '1200px', mx: 'auto', py: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ══ Section 1: Service Catalog ══════════════════════════════════════════ */}
            <Accordion
                defaultExpanded={false}
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    '&:before': { display: 'none' } // Remove MUI default border top line
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DynamicIcon name="Extension" className="h-6 w-6" style={{ color: 'text.primary' }} />
                        <Box>
                            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Integration Services Master</Typography>
                            <Typography variant="body2" color="text.secondary">Manage the central catalog of API services and definitions accessible across the platform.</Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, bgcolor: 'background.paper' }}>
                    <ServiceTypesManager token={token} embedded />
                </AccordionDetails>
            </Accordion>

            {/* ══ Section 2: Platform API Keys ══════════════════════════════════════════ */}
            <Accordion
                defaultExpanded={true}
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    '&:before': { display: 'none' }
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DynamicIcon name="Key" className="h-6 w-6" style={{ color: 'text.primary' }} />
                        <Box>
                            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Platform Integrations</Typography>
                            <Typography variant="body2" color="text.secondary">Configure API credentials and connections explicitly managed at the global application level.</Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, bgcolor: 'background.paper' }}>
                    <PlatformApiKeysSection token={token} embedded />
                </AccordionDetails>
            </Accordion>

        </Box >
    );
};

export default GlobalIntegrationsSettings;
